/**
 * Host-scoped request headers (APIM subscription key and any other header ref)
 *
 * Every VITE_APIM_<REF>_{ENABLED,NAME,VALUE,URL} group in the build-time env becomes one rule.
 * A rule is published only when ENABLED (optional; absent = on) is "true" and NAME, VALUE and
 * URL are all set and valid. The container publishes the rules once at startup; its own axios,
 * every module's axios and the fe-tools clients then add the header only to requests whose URL
 * matches the rule's origin + path prefix (see @sdx/b2o-fe-tools/httpHeaders).
 *
 * Values are filled in by the DevOps replace-variable-tokens step (*_SUBSCRIPTION_KEY comes from
 * Key Vault). They are inlined into the public JS bundle, so this is not a place for secrets that
 * must stay server-side.
 */

import {
  isConfiguredValue,
  parseRequestHeaderRules,
  publishHostRequestHeaderRules,
} from '@sdx/b2o-fe-tools/httpHeaders';
import type { RawRequestHeaderRule, RequestHeaderRule } from '@sdx/b2o-fe-tools/httpHeaders';

const APIM_ENV_KEY = /^VITE_APIM_([A-Z0-9_]+)_(ENABLED|NAME|VALUE|URL)$/;

type RefPart = 'ENABLED' | 'NAME' | 'VALUE' | 'URL';
type RefParts = Partial<Record<RefPart, string>>;

// ENABLED is optional for generic refs. When present it must be "true": "false", an unreplaced
// {{TOKEN}} or <MISSING_CONFIGURATION_VALUE!> all mean off.
const isRefEnabled = (enabled: string | undefined): boolean =>
  enabled === undefined || enabled.trim().toLowerCase() === 'true';

/**
 * Groups the VITE_APIM_<REF>_* env keys by <REF> and returns the validated header rules.
 * Takes the env as a parameter so tests can pass a plain object.
 */
export const buildApimHeaderRules = (env: Record<string, unknown>): RequestHeaderRule[] => {
  const refs = new Map<string, RefParts>();
  Object.entries(env).forEach(([key, value]) => {
    const match = APIM_ENV_KEY.exec(key);
    if (match === null || typeof value !== 'string') return;
    const ref = match[1];
    const part = match[2] as RefPart;
    refs.set(ref, { ...refs.get(ref), [part]: value });
  });

  const raws: RawRequestHeaderRule[] = [];
  refs.forEach((parts, ref) => {
    if (!isRefEnabled(parts.ENABLED)) return;
    if (!isConfiguredValue(parts.VALUE)) {
      // Expected when a family is switched off: the token step then writes an empty value.
      // Never log the value itself.
      if (parts.ENABLED !== undefined) {
        console.info(`[apim] header ref ${ref} has no value; skipped`);
      }
      return;
    }
    raws.push({ name: parts.NAME, value: parts.VALUE, domain: parts.URL });
  });

  return parseRequestHeaderRules(raws);
};

/**
 * Publishes the rules for the container and every module. Call once, before rendering.
 */
export const publishApimHeaderRules = (env: Record<string, unknown> = import.meta.env): void => {
  publishHostRequestHeaderRules(buildApimHeaderRules(env));
};
